import { randomUUID } from 'node:crypto';
import fs from 'node:fs';
import type { Readable, Writable } from 'node:stream';
import { StdioServerTransport } from '@modelcontextprotocol/sdk/server/stdio.js';
import type { JSONRPCMessage } from '@modelcontextprotocol/sdk/types.js';
import type { TraceMcpConfig } from '../../config.js';
import { logger } from '../../logger.js';
import { checkVersionDrift, versionDriftMessage } from '../../init/version-stamp.js';
import { stripRedundantSchemaKeyword } from '../../server/schema-shim.js';
import { ClientProfileGate } from '../../server/client-profile.js';
import { createToolFilter, resolveSessionPreset } from '../../server/tool-filter.js';
import { disarmStdoutGuard } from '../../server/transport-hardening.js';
import { armBoundedExit } from '../../server/bounded-shutdown.js';
import { startParentDeathWatch } from '../../server/parent-death-watch.js';
import { tryAutoSpawnDaemon } from '../lifecycle.js';
import { AutoRegisterNotice } from './auto-register-notice.js';
import { PollingDaemonWatcher } from './daemon-watcher.js';
import { recordSessionFallback } from './fallback-stats.js';
import {
  computeProxyTimeoutMs,
  hasStartupProgressChanged,
  probeProxyReadiness,
  resolveProxyInitializeTimeout,
  resolveProxyWarmupGrace,
  WARMUP_PROGRESS_SLICE_MS,
  type StartupProgress,
} from './proxy-timeout.js';
import {
  createHandshakeWatchdog,
  type HandshakeWatchdog,
  resolveHandshakeTimeout,
} from './handshake-watchdog.js';
// Type-only: the value is dynamic-imported in buildLocalBackend() so a
// proxy-only session (the common case once a daemon is up) never pulls in
// LocalBackend's dependency tree (PluginRegistry, better-sqlite3, tree-sitter,
// the full MCP tool surface) — see TRA-970.
import type { LocalBackend } from './local-backend.js';
import { MessageRouter } from './message-router.js';
import { ProxyBackend } from './proxy-backend.js';
// Type-only for the same reason as LocalBackend above: SnapshotBackend pulls
// in the identical heavy tree (PluginRegistry, better-sqlite3, createServer's
// full MCP tool surface) to open the daemon's index directly, and the
// "instant path" in bootstrap() below calls buildSnapshotBackend() on every
// session — a static import here would undo TRA-970's proxy.js size fix the
// moment TRA-948's snapshot path actually fires.
import type { SnapshotBackend } from './snapshot-backend.js';
import type { Backend } from './types.js';

declare const PKG_VERSION_INJECTED: string;
const PKG_VERSION =
  typeof PKG_VERSION_INJECTED !== 'undefined' ? PKG_VERSION_INJECTED : '0.0.0-dev';

const LIST_CHANGED = 'notifications/tools/list_changed';

/**
 * Owes the client exactly one `tools/list_changed` per `load_tools` call that
 * un-hid a profile-suppressed tool (TRA-796).
 *
 * The suppression lives on the wire, so nothing below the session knows the
 * surface changed and `load_tools` answers `already_loaded` without notifying.
 * But the same call can *also* load a preset-deferred tool, and that path does
 * notify — one request, two notifications, and a compliant host re-reads the
 * whole surface twice. So the backend's notification cancels the debt, and only
 * a call that produced none pays it, after its response.
 */
export class ListChangedDebt {
  private owedFor: string | number | null = null;

  /** Record that this request changed the advertised surface. */
  owe(id: string | number | undefined): void {
    if (id !== undefined) this.owedFor = id;
  }

  /** For each outbound frame: true when our own notification must follow it. */
  settle(frame: { id?: string | number; method?: string }): boolean {
    if (this.owedFor === null) return false;
    if (frame.method === LIST_CHANGED) {
      this.owedFor = null;
      return false;
    }
    if (frame.id !== this.owedFor) return false;
    this.owedFor = null;
    return true;
  }
}

function isInitializeRequest(msg: JSONRPCMessage): boolean {
  const m = msg as Record<string, unknown>;
  return m.method === 'initialize' && m.id !== undefined && m.id !== null;
}

export interface StdioSessionOptions {
  projectRoot: string;
  indexRoot: string;
  config: TraceMcpConfig;
  sharedDbPath: string;
  daemonPort: number;
  daemonUrl?: string;
  /** ms of stdin silence before we release full-mode resources. 0 = disabled. */
  idleTimeoutMs: number;
  /** ms the daemon state must be stable before we accept it. */
  daemonStabilityMs: number;
  /** ms to wait for pending requests to finish during a backend swap. */
  drainTimeoutMs?: number;
  /** If true and no daemon is running, try to spawn one before falling back to local mode. */
  autoSpawnDaemon?: boolean;
  /** ms to wait for an auto-spawned daemon's /health to respond. */
  autoSpawnTimeoutMs?: number;
  /**
   * Try SnapshotBackend's instant, read-only answer before negotiating the
   * real backend (TRA-948). Default true.
   *
   * Set false when the caller already has good reason to believe a daemon is
   * reachable (the thin proxy entry, TRA-970, only launches after the
   * launcher shim's own liveness check) — the snapshot's whole value is
   * covering an *uncertain* daemon state, and both it and `loadSnapshotBackend`
   * below can be skipped entirely.
   */
  trySnapshotFastPath?: boolean;
  /**
   * How to obtain the `SnapshotBackend` class when `trySnapshotFastPath`
   * needs it. Defaults to `await import('./snapshot-backend.js')`.
   *
   * SnapshotBackend pulls in the same heavy dependency tree as LocalBackend
   * (PluginRegistry, better-sqlite3, the full MCP tool surface) — TRA-948's
   * <400ms budget for the snapshot path only holds when that tree is already
   * resident in memory, which is true for `trace-mcp serve` (cli.js already
   * statically imports the same tree for its other commands) but not for the
   * thin proxy entry (TRA-970), which must not load it at all unless asked.
   * `trace-mcp serve` (src/cli.ts) passes a loader that resolves instantly
   * from its own already-loaded static import instead of relying on the
   * default's dynamic import, which — same as `buildLocalBackend()` below —
   * is a genuine first-load the moment anything actually calls it.
   */
  loadSnapshotBackend?: () => Promise<{
    SnapshotBackend: typeof import('./snapshot-backend.js').SnapshotBackend;
  }>;
  /**
   * Wall-clock budget for the MCP client to send its first JSON-RPC frame
   * after stdio comes up. If exceeded, write a one-line diagnostic to stderr
   * pointing at the most common failure modes (stdout-corruption from npm/
   * pnpm/uvx output, wrong binary path). Best-effort — server keeps running.
   * Defaults to env TRACE_MCP_HANDSHAKE_TIMEOUT or 5_000. Set 0 to disable.
   */
  handshakeTimeoutMs?: number;
  /** Overrides for the stdio streams. Defaults to the process's own. */
  stdin?: Readable;
  stdout?: Writable;
  /**
   * Base budget for the daemon to answer `initialize` before this session
   * falls back to local mode (TRA-1605). Defaults to env
   * TRACE_MCP_PROXY_INITIALIZE_TIMEOUT or 1_000. The effective deadline can
   * only grow past this when /health proves the daemon is alive-but-slow or
   * warming up — a silent daemon keeps the base, and a healthy daemon's
   * handshake timing is unchanged.
   */
  proxyInitializeTimeoutMs?: number;
  /**
   * Extra wait granted when /health reports the daemon is still starting up.
   * Defaults to env TRACE_MCP_PROXY_WARMUP_GRACE_MS or 20_000. A `starting`
   * daemon is granted this grace in slices (see maybeExtendProxyDeadline):
   * the session keeps waiting only while /health proves startup is
   * converging, and falls back to local mode at the first stalled slice —
   * a startup that outlasts the client's own timeout must never pin the
   * handshake for the whole grace (TRA-1844). Every session that is still
   * waiting at any moment waits on the one warming daemon instead of each
   * forking a local backend (cross-process single-flight).
   */
  proxyWarmupGraceMs?: number;
}

/**
 * Owns the singleton StdioServerTransport and the MessageRouter.
 * Decides which Backend to run based on daemon availability and orchestrates
 * promote/demote transitions.
 *
 * One StdioSession per process.
 */
export class StdioSession {
  private readonly opts: StdioSessionOptions;
  private readonly clientId = randomUUID();
  private readonly stdio: StdioServerTransport;
  private readonly router: MessageRouter;
  private readonly watcher: PollingDaemonWatcher;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private handshake: HandshakeWatchdog | null = null;
  private readonly pendingBackgroundDisposes = new Set<Promise<void>>();
  private shuttingDown = false;
  private bootstrapped = false;
  /** Tracks the mode we *intend* to have — may differ briefly from router.getActiveKind() during swap. */
  private desiredMode: 'proxy' | 'local' | 'snapshot' | 'dormant' = 'dormant';
  /** Guards against concurrent wakeUp() calls when multiple stdin messages arrive in the dormant window. */
  private wakePromise: Promise<void> | null = null;
  /**
   * The client's `initialize` frame, cached the moment it arrives. A swapped-in
   * ProxyBackend (local→proxy after daemon recovery) never sees `initialize`
   * again, so we seed it from here — otherwise its first request POSTs
   * session-less and surfaces "Session expired, reinitialize required" (#209).
   */
  private cachedInitialize: JSONRPCMessage | null = null;
  /**
   * Tailors the advertised surface to the connected host (TRA-513). Applied on
   * the wire because both backends build their tool surface and their
   * instructions before `initialize` has been read — this is the only layer that
   * sees the handshake and every frame going back to the client.
   */
  private readonly clientProfile: ClientProfileGate;
  /**
   * Tells the client, once, when this session's root was registered and
   * indexed by the daemon rather than by the user (#936). Constructed before
   * the handshake so it can see the registry as it was on arrival.
   */
  private readonly autoRegisterNotice: AutoRegisterNotice;
  /** One `tools/list_changed` per profile reinstatement, never two (TRA-796). */
  private readonly listChanged = new ListChangedDebt();
  /** Id of the in-flight `initialize` request the watchdog below is timing. */
  private initializeId: string | number | null = null;
  private initializeTimer: ReturnType<typeof setTimeout> | null = null;
  /** Wall-clock the current proxy watchdog was armed (for adaptive extension). */
  private proxyArmedAt = 0;
  /** Base proxy-initialize budget in effect for the current watchdog. */
  private proxyBaseMs = 0;
  /**
   * Absolute deadline the watchdog must not fall back before. Starts at
   * armedAt + base and only ever moves forward, when a /health probe proves
   * the daemon is alive-but-slow or still warming up (TRA-1605).
   */
  private proxyDeadline = 0;
  /**
   * Set once a daemon has failed to answer `initialize` for this session.
   * Blocks the watcher from swapping us back onto it — a daemon whose /health
   * works while its MCP handler is wedged would otherwise be re-adopted on the
   * next poll and hang the session all over again.
   */
  private proxyDisabled = false;

  constructor(opts: StdioSessionOptions) {
    this.opts = opts;
    this.clientProfile = new ClientProfileGate(opts.config);
    this.autoRegisterNotice = new AutoRegisterNotice(opts.projectRoot);
    this.stdio = stripRedundantSchemaKeyword(new StdioServerTransport(opts.stdin, opts.stdout));
    this.router = new MessageRouter({
      sendToClient: (msg) => {
        const id = (msg as unknown as { id?: string | number }).id;
        if (id !== undefined && id === this.initializeId) {
          this.clearInitializeWatchdog();
          // A daemon that fails the handshake fast is the same problem as one
          // that never answers, and gets the same answer: swallow its error
          // and replay the handshake locally rather than failing the client.
          if (Object.hasOwn(msg as object, 'error')) {
            void this.fallbackToLocal(id, 'proxy-initialize-error');
            return;
          }
        }
        return this.sendAndSettleListChanged(msg);
      },
      drainTimeoutMs: opts.drainTimeoutMs ?? 5_000,
      onSendFailure: (msg, err) => this.rescueFailedProxySend(msg, err),
    });
    this.watcher = new PollingDaemonWatcher({
      port: opts.daemonPort,
      stabilityMs: opts.daemonStabilityMs,
    });
  }

  /**
   * Start the session: pick initial mode based on daemon state, wire stdio,
   * start the daemon watcher, install idle + lifecycle hooks.
   */
  async bootstrap(): Promise<void> {
    if (this.bootstrapped) return;
    this.bootstrapped = true;

    // Wire the stdio transport so inbound messages go to the router.
    this.stdio.onmessage = (msg) => {
      this.handshake?.observe();
      this.resetIdleTimer();
      // Cache the handshake so a later swapped-in proxy backend can re-establish
      // the daemon session (the client only sends `initialize` once).
      if (isInitializeRequest(msg as JSONRPCMessage)) this.cachedInitialize = msg as JSONRPCMessage;
      // Reinstating a profile-suppressed tool changes what tools/list would
      // answer, and nothing below this layer knows that — the tool was never
      // deregistered, only hidden here. Owe the client one re-read, and settle
      // the debt when the call answers (TRA-796).
      if (this.clientProfile.observeFromClient(msg)) {
        this.listChanged.owe((msg as unknown as { id?: string | number }).id);
      }
      if (isInitializeRequest(msg as JSONRPCMessage)) {
        logger.info({ profile: this.clientProfile.name }, 'StdioSession: resolved client profile');
        this.armInitializeWatchdog((msg as unknown as { id: string | number }).id);
      }
      void this.router.ingestFromClient(msg as JSONRPCMessage);
    };
    this.stdio.onerror = (err) => {
      logger.warn({ err: String(err) }, 'StdioSession: stdio transport error');
    };

    // Instant path (TRA-948): if the daemon has already indexed this project,
    // its snapshot is sitting on disk — serve the client's first requests
    // from it directly instead of blocking stdio on the daemon /health check,
    // HTTP session registration, and SSE handshake (measured 2.7-3.6s cold,
    // TRA-931). The real backend is negotiated off the critical path below
    // and takes over via MessageRouter.swap() the moment it's ready. Skipped
    // when the caller already trusts the daemon is up (TRA-970) — see
    // `trySnapshotFastPath` on StdioSessionOptions.
    const snapshot =
      this.opts.trySnapshotFastPath !== false ? await this.buildSnapshotBackend() : null;
    if (snapshot) {
      try {
        await snapshot.start();
        this.router.setInitialBackend(snapshot);
        this.desiredMode = 'snapshot';
        await this.finishBootstrap();
        void this.settleRealBackend();
        return;
      } catch (err) {
        logger.warn(
          { err: String(err) },
          'StdioSession: snapshot backend failed to start, falling back to full bootstrap',
        );
        try {
          await snapshot.stop();
        } catch {
          /* best-effort */
        }
      }
    }

    const { backend: initialBackend, daemonActive } = await this.selectAndStartBackend();
    this.router.setInitialBackend(initialBackend);
    this.desiredMode = initialBackend.kind;
    this.watcher.onStableChange((nowActive) => {
      void this.onDaemonStateChange(nowActive);
    });
    this.resetIdleTimer();
    await this.finishBootstrap();

    // Now that the client can be answered, try to bring a daemon up. Success
    // is picked up by the watcher, which swaps this session onto a proxy
    // backend; failure just leaves us in local mode.
    if (!daemonActive && this.opts.autoSpawnDaemon !== false) {
      void this.backgroundAutoSpawn();
    }
  }

  /**
   * Poll the daemon and start whichever backend its state implies. This is
   * the slow path: `watcher.start()` blocks on a real `/health` round trip,
   * and a proxy backend blocks on HTTP session registration + SSE handshake.
   */
  private async selectAndStartBackend(): Promise<{ backend: Backend; daemonActive: boolean }> {
    await this.watcher.start();
    const daemonActive = this.watcher.getCurrentState();

    // Pick a backend from the daemon state we already know. Spawning a daemon
    // is an optimization and must never gate the handshake (TRA-704) — it runs
    // in the background, and the watcher swaps us onto it when it lands.
    let backend: Backend = daemonActive ? this.buildProxyBackend() : await this.buildLocalBackend();
    try {
      await backend.start();
    } catch (err) {
      if (backend.kind === 'local') throw err;
      // Daemon answered /health but wouldn't take the session — serve this
      // session in-process rather than failing the whole connection.
      logger.warn(
        { err: String(err) },
        'StdioSession: proxy backend failed to start, falling back to local mode',
      );
      backend = await this.buildLocalBackend();
      await backend.start();
    }
    return { backend, daemonActive };
  }

  /**
   * Runs after the snapshot backend has already answered the client's first
   * requests and stdio is live: negotiate the real backend the same way the
   * no-snapshot path does, then hand off via `swapTo()` — which drains
   * in-flight requests, replays the cached `initialize` into the new backend
   * (seeded via `cachedInitialize`, see buildProxyBackend), and stops (closes)
   * the snapshot's readonly connection. Mirrors onDaemonStateChange: the
   * candidate backend is handed to `swapTo` *unstarted* — `router.swap()`
   * starts it — instead of pre-starting it here and starting it again.
   */
  private async settleRealBackend(): Promise<void> {
    await this.watcher.start();
    const daemonActive = this.watcher.getCurrentState();
    await this.swapTo(
      daemonActive ? this.buildProxyBackend() : await this.buildLocalBackend(),
      'snapshot-settled',
    );
    // Daemon answered /health but wouldn't take the session — swapTo() already
    // logged and cleaned up the failed attempt; retry once in-process rather
    // than leaving the session stuck on the read-only snapshot forever.
    if (daemonActive && this.router.getActiveKind() !== 'proxy') {
      await this.swapTo(await this.buildLocalBackend(), 'snapshot-settled-fallback');
    }
    this.watcher.onStableChange((nowActive) => {
      void this.onDaemonStateChange(nowActive);
    });
    this.resetIdleTimer();
    if (!daemonActive && this.opts.autoSpawnDaemon !== false) {
      void this.backgroundAutoSpawn();
    }
  }

  /**
   * Everything after a backend is active: release the early-init stdout
   * guard, start the stdio transport, the version-drift probe, the handshake
   * watchdog, and the bootstrap log line. Runs exactly once per session,
   * regardless of which path picked the first backend.
   */
  private async finishBootstrap(): Promise<void> {
    // Release the early-init stdout guard now that the MCP transport owns
    // the stream. Anything that writes to stdout from this point on is
    // expected to be a JSON-RPC frame.
    disarmStdoutGuard();
    await this.stdio.start();
    // Drift probe: warn (stderr-only) if installed version diverges from
    // the version that last ran `trace-mcp init`. Best-effort; no-op when
    // dev build (PKG_VERSION_INJECTED unset) or when no stamp file exists.
    if (PKG_VERSION !== '0.0.0-dev') {
      const drift = checkVersionDrift(PKG_VERSION);
      if (drift.drift) {
        try {
          process.stderr.write(versionDriftMessage(drift));
        } catch {
          /* best-effort */
        }
      }
    }
    this.handshake = createHandshakeWatchdog({
      timeoutMs: resolveHandshakeTimeout(
        this.opts.handshakeTimeoutMs,
        process.env.TRACE_MCP_HANDSHAKE_TIMEOUT,
      ),
      write: (line) => {
        // stderr only — stdout would itself corrupt the JSON-RPC stream
        try {
          process.stderr.write(line);
        } catch {
          /* best-effort */
        }
      },
    });
    logger.info(
      {
        mode: this.desiredMode,
        projectRoot: this.opts.projectRoot,
        idleTimeoutMs: this.opts.idleTimeoutMs,
        daemonStabilityMs: this.opts.daemonStabilityMs,
      },
      'StdioSession bootstrapped',
    );
  }

  /**
   * Spawn a daemon off the critical path. A daemon that is starting up or
   * busy indexing can take minutes to answer /health — awaiting that before
   * `stdio.start()` used to leave `initialize` unanswered past the client's
   * MCP startup timeout, so the whole server came up as `failed` (TRA-704).
   */
  private async backgroundAutoSpawn(): Promise<void> {
    const spawnTimeoutMs = this.opts.autoSpawnTimeoutMs ?? 5_000;
    logger.info(
      { port: this.opts.daemonPort, timeoutMs: spawnTimeoutMs },
      'StdioSession: attempting daemon auto-spawn in background',
    );
    try {
      const result = await tryAutoSpawnDaemon(this.opts.daemonPort, spawnTimeoutMs);
      if (result.ok) {
        logger.info(
          { alreadyRunning: result.alreadyRunning },
          'StdioSession: daemon is reachable after auto-spawn',
        );
      } else {
        logger.warn(
          { error: result.error },
          'StdioSession: daemon auto-spawn failed, staying in local mode',
        );
      }
    } catch (err) {
      logger.warn({ err: String(err) }, 'StdioSession: daemon auto-spawn threw');
    }
  }

  /**
   * Graceful shutdown. Stops watcher, disposes active backend, clears timers,
   * waits for any in-flight background disposals (e.g. indexing) to settle.
   */
  async shutdown(reason: string): Promise<void> {
    if (this.shuttingDown) return;
    this.shuttingDown = true;
    logger.info({ reason }, 'StdioSession: shutting down');

    this.watcher.stop();
    if (this.idleTimer) clearTimeout(this.idleTimer);
    this.idleTimer = null;
    this.handshake?.cancel();
    this.handshake = null;
    this.clearInitializeWatchdog();

    const active = this.router.getActiveBackend();
    await this.router.shutdown();
    if (active?.backgroundDispose) this.pendingBackgroundDisposes.add(active.backgroundDispose);

    // Give background cleanups a reasonable chance, but don't hang forever.
    if (this.pendingBackgroundDisposes.size > 0) {
      const all = Promise.allSettled([...this.pendingBackgroundDisposes]);
      await Promise.race([
        all,
        new Promise<void>((resolve) => {
          const t = setTimeout(() => resolve(), 5_000);
          t.unref?.();
        }),
      ]);
    }

    try {
      await this.stdio.close();
    } catch {
      /* best-effort */
    }
  }

  // ── Internals ───────────────────────────────────────────────────────

  /** Send one frame, then any `tools/list_changed` it left owed (TRA-796). */
  private async sendAndSettleListChanged(msg: unknown): Promise<void> {
    const owed = this.listChanged.settle(msg as { id?: string | number; method?: string });
    const shaped = this.autoRegisterNotice.applyTo(this.clientProfile.applyToClient(msg));
    await this.stdio.send(shaped as JSONRPCMessage);
    if (owed) await this.stdio.send({ jsonrpc: '2.0', method: LIST_CHANGED } as JSONRPCMessage);
  }

  /**
   * A reachable /health is not proof that the daemon's MCP handler can answer.
   * Give the proxy a bounded window to complete the handshake; if it misses,
   * serve the session in-process and replay the frame there, so the client
   * gets a real `initialize` result instead of a hung connection (TRA-704).
   *
   * The window is adaptive (TRA-1605): a /health probe that shows the daemon
   * warming up or loaded pushes the deadline forward instead of stampeding N
   * sessions into local mode at the same fixed instant. A silent daemon keeps
   * the base budget, so the dead-daemon fast path is unchanged.
   */
  private armInitializeWatchdog(id: string | number): void {
    this.clearInitializeWatchdog();
    if (this.router.getActiveKind() !== 'proxy') return;
    this.initializeId = id;
    const baseMs = resolveProxyInitializeTimeout(
      this.opts.proxyInitializeTimeoutMs,
      process.env.TRACE_MCP_PROXY_INITIALIZE_TIMEOUT,
    );
    this.proxyArmedAt = Date.now();
    this.proxyBaseMs = baseMs;
    this.proxyDeadline = this.proxyArmedAt + baseMs;
    this.initializeTimer = setTimeout(() => {
      void this.onProxyWatchdogFire(id);
    }, baseMs);
    this.initializeTimer.unref?.();
    void this.maybeExtendProxyDeadline(id, baseMs);
  }

  /**
   * One /health probe per handshake: extend the deadline when the daemon is
   * provably alive-but-slow or still warming up. Never shortens it, never
   * throws, and no-ops when the handshake already settled.
   *
   * The warming-up branch grants the grace in slices (TRA-1844): one slice
   * is extended immediately, and each further slice only when a re-probe
   * shows startup converging. A stalled startup — same progress tuple twice
   * in a row — falls back at the slice instead of burning the whole grace
   * past the client's own startup timeout.
   */
  private async maybeExtendProxyDeadline(id: string | number, baseMs: number): Promise<void> {
    let readiness: Awaited<ReturnType<typeof probeProxyReadiness>> = null;
    try {
      readiness = await probeProxyReadiness(this.opts.daemonPort);
    } catch {
      return;
    }
    if (id !== this.initializeId || this.router.getActiveKind() !== 'proxy') return;
    if (!readiness) return;
    const warmupGraceMs = resolveProxyWarmupGrace(
      this.opts.proxyWarmupGraceMs,
      process.env.TRACE_MCP_PROXY_WARMUP_GRACE_MS,
    );
    if (!readiness.starting) {
      const totalMs = computeProxyTimeoutMs(baseMs, readiness, warmupGraceMs);
      if (totalMs <= baseMs) return;
      const deadline = this.proxyArmedAt + totalMs;
      if (deadline <= Date.now()) return;
      this.proxyDeadline = deadline;
      if (this.initializeTimer) clearTimeout(this.initializeTimer);
      this.initializeTimer = setTimeout(
        () => {
          void this.onProxyWatchdogFire(id);
        },
        Math.max(0, deadline - Date.now()),
      );
      this.initializeTimer.unref?.();
      logger.info(
        {
          id,
          baseMs,
          totalMs,
          starting: readiness.starting,
          healthRttMs: readiness.rttMs,
        },
        'StdioSession: daemon is loaded but reachable — extending the proxy handshake deadline',
      );
      return;
    }
    this.watchStartupProgress(id, baseMs, warmupGraceMs, readiness.progress, true);
  }

  /**
   * Warmup slice loop (TRA-1844). Extends the proxy handshake deadline by one
   * slice and re-probes at its end: keep waiting while the daemon's startup
   * progress moves, fall back the moment it stalls, and never wait past
   * armedAt + base + grace. Daemons without a progress signal (legacy/fake
   * health payloads) get the remaining grace in one shot — absence of the
   * signal is not evidence of a stall.
   */
  private watchStartupProgress(
    id: string | number,
    baseMs: number,
    warmupGraceMs: number,
    lastProgress: StartupProgress | undefined,
    firstSlice: boolean,
  ): void {
    if (id !== this.initializeId || this.router.getActiveKind() !== 'proxy') return;
    const capTotal = computeProxyTimeoutMs(
      baseMs,
      { reachable: true, starting: true, rttMs: 0 },
      warmupGraceMs,
    );
    const capAt = this.proxyArmedAt + capTotal;
    const sliceAt = Math.min(Date.now() + WARMUP_PROGRESS_SLICE_MS, capAt);
    if (sliceAt <= Date.now()) {
      // Cap reached. On the first slice the base watchdog is still armed and
      // will fall back on its own — stay silent. Deeper in the chain the
      // slice timers replaced it, so no timer would ever fire again: fall
      // back now rather than hanging the handshake (review, PR #1360).
      if (!firstSlice) void this.fallbackToLocal(id, 'proxy-initialize-timeout');
      return;
    }
    this.proxyDeadline = sliceAt;
    if (this.initializeTimer) clearTimeout(this.initializeTimer);
    if (firstSlice) {
      logger.info(
        { id, baseMs, totalMs: capTotal, starting: true },
        'StdioSession: daemon is warming up — waiting instead of falling back to local mode',
      );
    }
    this.initializeTimer = setTimeout(
      () => {
        void this.onStartupSlice(id, baseMs, warmupGraceMs, capAt, lastProgress);
      },
      Math.max(0, sliceAt - Date.now()),
    );
    this.initializeTimer.unref?.();
  }

  /** One warmup slice elapsed: re-probe and decide whether to keep waiting. */
  private async onStartupSlice(
    id: string | number,
    baseMs: number,
    warmupGraceMs: number,
    capAt: number,
    lastProgress: StartupProgress | undefined,
  ): Promise<void> {
    if (id !== this.initializeId || this.router.getActiveKind() !== 'proxy') return;
    let readiness: Awaited<ReturnType<typeof probeProxyReadiness>> = null;
    try {
      readiness = await probeProxyReadiness(this.opts.daemonPort);
    } catch {
      readiness = null;
    }
    if (id !== this.initializeId || this.router.getActiveKind() !== 'proxy') return;
    if (!readiness) {
      // The daemon died mid-wait: the dead-daemon fast path, not the grace.
      await this.fallbackToLocal(id, 'proxy-initialize-timeout');
      return;
    }
    if (!readiness.starting) {
      // Ready now: the in-flight proxied handshake may complete at any
      // moment, so grant it one last slice bounded by the cap, then fall
      // back. A full remainder here would reintroduce the TRA-1844 hang for
      // a daemon whose /health is ready while its MCP handler is still slow.
      const deadline = Math.min(Date.now() + WARMUP_PROGRESS_SLICE_MS, capAt);
      if (deadline <= Date.now()) {
        await this.fallbackToLocal(id, 'proxy-initialize-timeout');
        return;
      }
      this.proxyDeadline = deadline;
      if (this.initializeTimer) clearTimeout(this.initializeTimer);
      this.initializeTimer = setTimeout(
        () => {
          void this.onProxyWatchdogFire(id);
        },
        Math.max(0, deadline - Date.now()),
      );
      this.initializeTimer.unref?.();
      return;
    }
    if (readiness.progress === undefined) {
      // No progress signal (legacy/fake health payloads) — grant the
      // remainder in one shot. Absence of the signal is not evidence of a
      // stall, so the legacy path keeps the pre-TRA-1844 behavior.
      const totalMs = Math.min(
        computeProxyTimeoutMs(baseMs, readiness, warmupGraceMs),
        Math.max(baseMs, capAt - this.proxyArmedAt),
      );
      if (totalMs <= baseMs) {
        await this.fallbackToLocal(id, 'proxy-initialize-timeout');
        return;
      }
      const deadline = this.proxyArmedAt + totalMs;
      if (deadline <= Date.now()) {
        await this.fallbackToLocal(id, 'proxy-initialize-timeout');
        return;
      }
      this.proxyDeadline = deadline;
      if (this.initializeTimer) clearTimeout(this.initializeTimer);
      this.initializeTimer = setTimeout(
        () => {
          void this.onProxyWatchdogFire(id);
        },
        Math.max(0, deadline - Date.now()),
      );
      this.initializeTimer.unref?.();
      return;
    }
    if (!hasStartupProgressChanged(lastProgress, readiness.progress)) {
      logger.warn(
        { id, progress: readiness.progress },
        'StdioSession: daemon startup stalled — falling back to local mode instead of waiting out the warmup grace',
      );
      await this.fallbackToLocal(id, 'proxy-startup-stalled');
      return;
    }
    this.watchStartupProgress(id, baseMs, warmupGraceMs, readiness.progress, false);
  }

  /** The watchdog timer fired: re-arm when an extension moved the deadline, else fall back. */
  private async onProxyWatchdogFire(id: string | number): Promise<void> {
    if (id !== this.initializeId) return;
    const remaining = this.proxyDeadline - Date.now();
    if (remaining > 50) {
      // maybeExtendProxyDeadline pushed the deadline after this timer was
      // scheduled (or re-armed an older one) — wait out the remainder rather
      // than falling back early.
      if (this.initializeTimer) clearTimeout(this.initializeTimer);
      this.initializeTimer = setTimeout(() => {
        void this.onProxyWatchdogFire(id);
      }, remaining);
      this.initializeTimer.unref?.();
      return;
    }
    await this.fallbackToLocal(id, 'proxy-initialize-timeout');
  }

  private clearInitializeWatchdog(): void {
    if (this.initializeTimer) clearTimeout(this.initializeTimer);
    this.initializeTimer = null;
    this.initializeId = null;
    this.proxyArmedAt = 0;
    this.proxyBaseMs = 0;
    this.proxyDeadline = 0;
  }

  /**
   * Take the handshake away from a daemon that could not complete it — it
   * either timed out or answered with an error — and finish it in-process by
   * replaying the cached frame through a local backend.
   */
  private async fallbackToLocal(id: string | number, reason: string): Promise<void> {
    // Read before clearInitializeWatchdog() zeroes it below.
    const baseMs = this.proxyBaseMs;
    this.clearInitializeWatchdog();
    if (this.shuttingDown || this.router.getActiveKind() !== 'proxy') return;
    // Count every proxy→local demotion so a fallback storm is visible in
    // `daemon stats` instead of something to guess about (TRA-1605).
    recordSessionFallback(reason);
    logger.warn(
      { id, reason, timeoutMs: baseMs },
      'StdioSession: daemon did not complete initialize — serving this session in local mode',
    );
    // Build BEFORE claiming the id. This is a genuine first load off disk —
    // the thin proxy entry (TRA-970) never imports LocalBackend — so it can
    // fail, and the event that gets us here is often the same one that makes
    // it fail: a package swap (`npm i -g trace-mcp`, the app replacing its
    // bundle) removes dist/local-backend.js at the exact moment it unsettles
    // the daemon. Disk full and a native-module load failure land here too.
    //
    // Claiming the id first meant that throw orphaned the handshake: nobody
    // answered it, the process safety net swallowed the rejection, and the
    // client hung until its own timeout and reported `Failed to connect` —
    // the outcome this watchdog exists to prevent (TRA-1148).
    let local: LocalBackend;
    try {
      local = await this.buildLocalBackend();
    } catch (err) {
      logger.error(
        { id, reason, err: String(err) },
        'StdioSession: local-mode fallback could not be built — failing the handshake explicitly',
      );
      // Answer explicitly rather than through the router. On the timeout path
      // the id is still pending, but on the `proxy-initialize-error` path the
      // router already cleared it when the daemon's error response arrived —
      // this method having swallowed that error precisely so it could replay
      // the handshake locally. With the replay gone, the swallow has to be
      // undone, and only this side knows the id was ever ours.
      //
      // An error the client can surface beats a connection that never
      // resolves: clients retry, and by then the swap window has closed.
      this.router.forgetPending(id);
      // Detach the backend we are answering over the top of. Every other path
      // that answers a stuck id reaches `router.swap()`, whose step 3 does
      // exactly this before anything else is forwarded; this branch is the one
      // that answers without swapping, so it has to do it itself. Otherwise a
      // merely slow daemon — the `proxy-initialize-timeout` trigger, as
      // opposed to a dead one — delivers its real `initialize` response after
      // we gave up, `wireBackend` forwards it, and the client gets two
      // responses for one id. The interceptor's `id === this.initializeId`
      // guard does not catch it: `clearInitializeWatchdog()` nulled that at
      // the top of this method.
      //
      // Unconditional is safe here: this only ever runs for `initialize`, so
      // no other in-flight id shares this backend yet.
      const stale = this.router.getActiveBackend();
      if (stale) stale.onmessage = undefined;
      await this.sendAndSettleListChanged({
        jsonrpc: '2.0',
        id,
        error: { code: -32603, message: `Local fallback unavailable: ${String(err)}` },
      } as unknown as JSONRPCMessage);
      return;
    }
    this.proxyDisabled = true;
    // Drop the in-flight id so the swap does not answer it with a synthetic
    // error; the replay below is the response the client actually receives.
    this.router.forgetPending(id);
    await this.swapTo(local, reason);
    if (this.router.getActiveKind() !== 'local' || !this.cachedInitialize) return;
    await this.router.ingestFromClient(this.cachedInitialize);
  }

  /**
   * A request whose send to the daemon threw: promote to local mode now and
   * replay the frame there (TRA-1080).
   *
   * `PollingDaemonWatcher` would get here eventually, but it is built to
   * debounce — 10 s poll plus a 30 s stability window — and that delay is only
   * free in the local → proxy direction. Going the other way, every request in
   * the window is answered `-32603 Backend send failed`, and an agent that
   * sees that stops calling the tools for the rest of the session. Measured on
   * a real daemon killed mid-session: 45 s, eight dead calls.
   *
   * `ProxyBackend.send` has already retried the transport three times by the
   * time it throws, so this is not a blip — it is the same evidence the poller
   * is still collecting, arriving earlier and for free. Deliberately *not*
   * setting `proxyDisabled`: unlike a daemon that fails the handshake, one that
   * merely died deserves to be proxied to again once the watcher sees it back.
   */
  private async rescueFailedProxySend(msg: JSONRPCMessage, err: unknown): Promise<boolean> {
    const id = (msg as { id?: string | number }).id;
    if (id === undefined || id === null) return false;
    if (this.shuttingDown || this.proxyDisabled) return false;
    if (this.router.getActiveKind() !== 'proxy') return false;
    recordSessionFallback('proxy-send-failed');
    logger.warn(
      { id, err: String(err) },
      'StdioSession: proxy send failed — promoting to local mode and replaying the request',
    );
    // Build before claiming the id, for the reason spelled out in
    // fallbackToLocal: this import can fail, and returning false with the id
    // still pending lets MessageRouter answer it with -32603 rather than
    // relying on the exact moment it last checked `pendingRequestIds`.
    let local: LocalBackend;
    try {
      local = await this.buildLocalBackend();
    } catch (buildErr) {
      logger.error(
        { id, err: String(buildErr) },
        'StdioSession: local-mode rescue could not be built — leaving the request for the router to fail',
      );
      return false;
    }
    // Claim the id before swapping, or the swap's drain answers it with a
    // synthetic error and the client gets two responses for one request.
    this.router.forgetPending(id);
    await this.swapTo(local, 'proxy-send-failed');
    if (this.router.getActiveKind() !== 'local') return false;
    await this.router.ingestFromClient(msg);
    return true;
  }

  private async onDaemonStateChange(nowActive: boolean): Promise<void> {
    if (this.shuttingDown) return;
    if (nowActive && this.proxyDisabled) return;
    const currentKind = this.router.getActiveKind();
    if (nowActive) {
      if (currentKind === 'proxy') return; // already proxying
      await this.swapTo(this.buildProxyBackend(), 'daemon-appeared');
    } else {
      if (currentKind === 'local') return; // already local
      // Same fallible import as the two paths above, and this one is called
      // fire-and-forget from the watcher, which reports a stable transition
      // exactly once — so a throw here used to leave the session pinned to a
      // dead daemon with nothing scheduled to try again. Swallow it: the next
      // failed send retries the build through rescueFailedProxySend, by which
      // time a swap window has closed (TRA-1148).
      recordSessionFallback('daemon-disappeared');
      let local: LocalBackend;
      try {
        local = await this.buildLocalBackend();
      } catch (err) {
        logger.error(
          { err: String(err) },
          'StdioSession: could not build local backend after the daemon disappeared — staying in proxy mode until the next request retries',
        );
        return;
      }
      await this.swapTo(local, 'daemon-disappeared');
    }
  }

  private async swapTo(next: Backend, reason: string): Promise<void> {
    logger.info({ reason, to: next.kind }, 'StdioSession: swapping backend');
    const prev = this.router.getActiveBackend();
    try {
      await this.router.swap(next);
      this.desiredMode = next.kind;
    } catch (err) {
      logger.error({ err: String(err) }, 'StdioSession: swap failed');
      // Try to still stop the new backend to avoid leaks.
      try {
        await next.stop();
      } catch {
        /* best-effort */
      }
      return;
    }
    if (prev?.backgroundDispose) {
      this.pendingBackgroundDisposes.add(prev.backgroundDispose);
      prev.backgroundDispose.finally(() =>
        this.pendingBackgroundDisposes.delete(prev.backgroundDispose!),
      );
    }
  }

  private resetIdleTimer(): void {
    if (this.opts.idleTimeoutMs <= 0) return;
    if (this.idleTimer) clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => {
      void this.onIdle();
    }, this.opts.idleTimeoutMs);
    this.idleTimer.unref?.();
    // If we were dormant (idle already released), wake up by reinstating the right backend.
    if (this.desiredMode === 'dormant' && !this.shuttingDown) {
      void this.wakeUp();
    }
  }

  private async onIdle(): Promise<void> {
    if (this.shuttingDown) return;
    const activeKind = this.router.getActiveKind();
    if (activeKind !== 'local') {
      // Proxy is already lightweight; nothing to release. Just keep the timer armed.
      this.resetIdleTimer();
      return;
    }
    logger.info('StdioSession: idle — releasing local backend resources');
    const prev = this.router.getActiveBackend();
    await this.router.shutdown(); // stops old backend, leaves no active backend
    if (prev?.backgroundDispose) {
      this.pendingBackgroundDisposes.add(prev.backgroundDispose);
      prev.backgroundDispose.finally(() =>
        this.pendingBackgroundDisposes.delete(prev.backgroundDispose!),
      );
    }
    this.desiredMode = 'dormant';
    // Note: router now has no active backend. ingestFromClient() will queue
    // messages until wakeUp() re-establishes one.
  }

  private wakeUp(): Promise<void> {
    if (this.wakePromise) return this.wakePromise;
    if (this.desiredMode !== 'dormant' || this.shuttingDown) return Promise.resolve();
    this.wakePromise = (async () => {
      try {
        logger.info('StdioSession: wake up from idle');
        const daemonActive = this.watcher.getCurrentState() && !this.proxyDisabled;
        const next = daemonActive ? this.buildProxyBackend() : await this.buildLocalBackend();
        await next.start();
        this.desiredMode = next.kind;
        this.router.setInitialBackend(next);
        await this.router.flushPending();
      } finally {
        this.wakePromise = null;
      }
    })();
    return this.wakePromise;
  }

  /** Tools this session escalated into via `load_tools` (TRA-402). */
  private readonly loadedTools = new Set<string>();
  /** Preset/include/exclude filter, before any `load_tools` escalation. */
  private baseFilter: ((name: string) => boolean) | null = null;

  private baseToolFilter(name: string): boolean {
    this.baseFilter ??= createToolFilter(this.opts.config);
    return this.baseFilter(name);
  }

  private buildProxyBackend(): ProxyBackend {
    const daemonUrl = this.opts.daemonUrl ?? `http://127.0.0.1:${this.opts.daemonPort}`;
    return new ProxyBackend({
      daemonUrl,
      projectRoot: this.opts.projectRoot,
      clientId: this.clientId,
      clientTransportKind: 'stdio-proxy',
      // Seed the cached handshake so a swap-in backend (the client already
      // initialized through a previous backend) can re-establish a daemon
      // session on its first request instead of surfacing "Session expired".
      // Null at bootstrap — the initial backend captures it via send() instead.
      initializeFrame: this.cachedInitialize ?? undefined,
      // The preset is a property of *this* client session, not of the daemon
      // (which serves every tool to everyone) — so it has to be applied here
      // on the way out, or it never reaches a daemon-backed client (TRA-250).
      toolFilter: (name) => this.baseToolFilter(name) || this.loadedTools.has(name),
      // TRA-951: so `get_preset_info` reports *this* session's preset instead
      // of the daemon's.
      presetName: resolveSessionPreset(this.opts.config).name,
      // TRA-402: `load_tools` is answered by the proxy, not the daemon — the
      // daemon serves one full surface to every session and has no idea which
      // tools *this* session has paid for. Escalation state lives on the
      // Session so it survives a backend swap after a daemon restart.
      toolSurface: {
        isExcluded: (name) => (this.opts.config.tools?.exclude ?? []).includes(name),
        load: (names) => {
          for (const name of names) this.loadedTools.add(name);
        },
      },
    });
  }

  private async buildLocalBackend(): Promise<LocalBackend> {
    const { LocalBackend } = await import('./local-backend.js');
    return new LocalBackend({
      projectRoot: this.opts.projectRoot,
      indexRoot: this.opts.indexRoot,
      config: this.opts.config,
      sharedDbPath: this.opts.sharedDbPath,
    });
  }

  /** Null when there's nothing on disk yet to read instantly (safe fallback, TRA-948). */
  private async buildSnapshotBackend(): Promise<SnapshotBackend | null> {
    if (!fs.existsSync(this.opts.sharedDbPath)) return null;
    const load = this.opts.loadSnapshotBackend ?? (() => import('./snapshot-backend.js'));
    const { SnapshotBackend } = await load();
    return new SnapshotBackend({
      projectRoot: this.opts.projectRoot,
      config: this.opts.config,
      sharedDbPath: this.opts.sharedDbPath,
    });
  }
}

/**
 * Standard process-lifecycle wiring for a stdio-transport MCP server process:
 * signal/stdin/parent-death shutdown handlers with a bounded hard-exit
 * fallback, then bootstrap. Resolves once stdio closes (the session ended
 * normally) — every exit path has already called `session.shutdown()`.
 *
 * Shared by the full CLI's `serve` command and the thin proxy entry
 * (src/proxy-entry.ts) so both processes exit exactly the same way (TRA-970).
 */
export async function runStdioSession(session: StdioSession): Promise<void> {
  let shuttingDown = false;
  const shutdown = async (reason: string) => {
    if (shuttingDown) return;
    shuttingDown = true;
    logger.info({ reason }, 'Shutting down trace-mcp server');
    // #236 defect 2: orphaned sessions survived SIGTERM because graceful
    // shutdown could hang — only SIGKILL worked. Arm a bounded hard-exit so a
    // wedged session cannot outlive its shutdown signal. Unref'd, so it never
    // keeps the process alive on its own.
    // TRA-849: exit 0 — every reason that reaches here is a requested stop
    // (signal, or the client closing stdin), not a crash.
    armBoundedExit(reason, {
      exitCode: 0,
      onTimeout: () => {
        logger.warn({ reason }, 'Session shutdown exceeded its deadline — forcing exit');
      },
    });
    try {
      await session.shutdown(reason);
    } catch (err) {
      logger.warn({ err: String(err) }, 'Session shutdown errored');
    }
    process.exit(0);
  };

  process.on('SIGINT', () => {
    void shutdown('SIGINT');
  });
  process.on('SIGTERM', () => {
    void shutdown('SIGTERM');
  });
  // Orphan prevention: when the MCP client exits, stdin closes.
  process.stdin.on('end', () => {
    void shutdown('stdin-end');
  });
  process.stdin.on('close', () => {
    void shutdown('stdin-close');
  });
  // #236 defect 1b: stdin close is the primary parent-death signal, but in the
  // field some orphans kept running with their stdin fd held open via an
  // inherited pipe. Poll ppid as a cheap fallback so a reparented-to-init
  // session shuts itself down.
  startParentDeathWatch((reason) => {
    logger.warn({ reason }, 'Detected orphaned session (parent died) — shutting down');
    void shutdown(reason);
  });

  await session.bootstrap();
  // bootstrap() called stdio.start() which resolves when stdin closes. The
  // process stays alive on the stdin event loop; shutdown handlers above take
  // care of exit.
}
